/* ===================== SETTINGS (pricing slabs) =====================
   SPECIAL CASE (temporary, TODO refactor into roles.js / back-end config):
   dedicated "Settings" sidebar tab, currently shown only to the accounts in
   DashboardPage's EXCLUSIVE_TABS_BY_EMAIL (rjn.bharti333@gmail.com).

   Tiered per-session pricing editor. Save: PATCH /astrologer/pricing-slabs
   { slab_1_price, slab_2_price, slab_3_price, is_active }. Prefill:
   GET on the same path — a failed read just starts the form blank
   instead of blocking.

   Registered under the tab key `pricing` (label "Settings") because the
   `settings` key is a legacy ?tab= alias of My Profile. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { useForm } from '@astro/forms'
import GemstoneConsultationSettings from './GemstoneConsultationSettings'

/** Unwrap a Laravel `{ status, data }` envelope (or pass a bare value through). */
function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res) return res.data
  return res
}

/** Normalise GET /astrologer/pricing-slabs into { slab_N_price, is_active }.
 * Live shape: { status, data: { astrologer_id, is_active, slabs: [
 *   { label, min_days, max_days, price }, … ] } } — rows map to slab 1/2/3
 * by ascending min_days (slab 1 = the soonest booking window). Flat
 * slab_N_price records (the PATCH body shape) pass through unchanged. */
function pickSlabRecord(raw) {
  let rec = unwrap(unwrap(raw))
  if (Array.isArray(rec)) rec = rec[0]
  if (!rec || typeof rec !== 'object') return {}
  const inner = rec.pricing_slabs ?? rec.pricing_slab
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) rec = inner
  if (Array.isArray(rec.slabs)) {
    const rows = [...rec.slabs].sort((a, b) => (a?.min_days ?? 0) - (b?.min_days ?? 0))
    return {
      is_active: rec.is_active,
      slab_1_price: rows[0]?.price,
      slab_2_price: rows[1]?.price,
      slab_3_price: rows[2]?.price,
    }
  }
  return rec
}

/* Booking-window semantics (business rule): customers may ONLY book in one
   of these three windows — no other slots are allowed — and the price is
   decided by how far in advance they book. */
const SLABS = [
  { name: 'slab_1_price', tier: 'Within 2 days', caption: 'Booked 0–2 days before the session', placeholder: '21000' },
  { name: 'slab_2_price', tier: '15–16 days ahead', caption: 'Booked 15 or 16 days in advance', placeholder: '15000' },
  { name: 'slab_3_price', tier: '45–46 days ahead', caption: 'Booked 45 or 46 days in advance', placeholder: '11000' },
]

/** '' for missing/non-numeric so inputs start blank, not "undefined"/NaN;
 * decimal strings like "21000.00" become "21000". */
const toPriceInput = (v) => {
  if (v == null || v === '') return ''
  const n = Number(v)
  return Number.isFinite(n) ? String(n) : ''
}

/** Laravel booleans arrive as true/false, 1/0 or "1"/"0" — Boolean("0") is
 * true, so string falsy spellings need explicit handling. */
const toBool = (v) => {
  if (typeof v === 'string') return !['0', 'false', ''].includes(v.trim().toLowerCase())
  return Boolean(v)
}

function buildPrefill(rec = {}) {
  return {
    slab_1_price: toPriceInput(rec.slab_1_price),
    slab_2_price: toPriceInput(rec.slab_2_price),
    slab_3_price: toPriceInput(rec.slab_3_price),
    is_active: rec.is_active != null ? toBool(rec.is_active) : true,
  }
}

function SettingsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Loading your settings">
      <div className="dash-card p-4 sm:p-6">
        <div className="h-4 w-44 bg-stone-100 rounded-full mb-2" />
        <div className="h-2.5 w-64 bg-stone-100 rounded-full mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="border border-stone-200 rounded-2xl p-5 space-y-3">
              <div className="h-2.5 w-16 bg-stone-100 rounded-full" />
              <div className="h-11 bg-stone-100 rounded-xl" />
              <div className="h-2 w-24 bg-stone-100 rounded-full" />
            </div>
          ))}
        </div>
      </div>
      <div className="dash-card p-4 sm:p-6 h-24" />
    </div>
  )
}

export default function PricingSettingsTab() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null) // { tone: 'ok' | 'err', text }

  /* Auto-dismissing success toast — the dashboard package has no shared
     toast system, so this stays local to the tab (.dash-toast in
     DashboardPage.css handles position + slide-up). */
  const [toast, setToast] = useState(null)
  const toastTimer = useRef(null)
  const showToast = (text) => {
    clearTimeout(toastTimer.current)
    setToast(text)
    toastTimer.current = setTimeout(() => setToast(null), 3500)
  }
  useEffect(() => () => clearTimeout(toastTimer.current), [])

  /* Prefill read — an error (e.g. no slabs saved yet) just leaves the
     form blank instead of blocking. */
  const slabsQuery = useQuery({
    queryKey: ['dashboard', 'pricing-slabs', user?.id],
    queryFn: () => api.astrologers.pricingSlabs(),
    enabled: Boolean(user?.id),
    retry: false,
    select: pickSlabRecord,
  })

  const prefill = useMemo(() => buildPrefill(slabsQuery.data ?? {}), [slabsQuery.data])
  const {
    register, handleSubmit, watch, setValue, setError,
    formState: { errors },
  } = useForm({
    defaultValues: prefill,
    values: prefill,
    resetOptions: { keepDirtyValues: true },
    mode: 'onTouched',
  })
  const isActive = watch('is_active')

  const saveMutation = useMutation({
    mutationFn: (payload) => api.astrologers.updatePricingSlabs(payload),
    onSuccess: () => {
      setBanner(null)
      showToast('Pricing updated successfully.')
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'pricing-slabs'] })
    },
    onError: (err) => {
      // Laravel 422s carry per-field messages under `details`; field names
      // match the payload keys 1:1, so they surface inline as-is.
      const details = err?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner({ tone: 'err', text: err?.message || 'Could not save your pricing. Please try again.' })
    },
  })

  const onSave = handleSubmit(
    (values) => {
      setBanner(null)
      saveMutation.mutate({
        slab_1_price: Number(values.slab_1_price),
        slab_2_price: Number(values.slab_2_price),
        slab_3_price: Number(values.slab_3_price),
        is_active: Boolean(values.is_active),
      })
    },
    () => setBanner({ tone: 'err', text: 'Please fix the highlighted fields before saving.' }),
  )

  if (!(slabsQuery.isSuccess || slabsQuery.isError)) return <SettingsSkeleton />

  const priceRules = () => ({
    required: 'Price is required.',
    min: { value: 0, message: 'Price cannot be negative.' },
  })

  return (
    <div className="space-y-6">
    <form onSubmit={onSave} className="space-y-6">
      {/* ── Pricing slabs ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-9 h-9 rounded-xl bg-[#F5E6D3] flex items-center justify-center text-base shrink-0">💰</span>
          <div>
            <h3 className="text-lg text-astroDark font-royal leading-tight">Session Pricing Slabs</h3>
            <p className="text-[11px] text-stone-500">Price depends on how far in advance a customer books — only these three booking windows are allowed.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
          {SLABS.map(({ name, tier, caption, placeholder }) => (
            <div key={name} className="border border-stone-200 rounded-2xl p-5 bg-stone-50/50 hover:border-goldAccent/40 transition-colors">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A67C00]">{tier}</p>
              <p className="text-[10px] text-stone-500 mt-0.5 mb-3">{caption}</p>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-stone-500 font-semibold pointer-events-none">₹</span>
                <input
                  type="number"
                  min={0}
                  placeholder={placeholder}
                  {...register(name, priceRules())}
                  className="w-full bg-white border border-stone-200 rounded-xl pl-8 pr-4 py-3 text-sm font-semibold text-astroDark tabular-nums focus:outline-none focus:border-goldAccent/50 transition-colors"
                />
              </div>
              {errors[name] && <p className="text-[10px] text-red-500 mt-1.5">{errors[name].message}</p>}
              <p className="text-[10px] text-stone-500 mt-2">Per session</p>
            </div>
          ))}
        </div>

        {/* ── Active toggle ── */}
        <div className="mt-5 flex items-center justify-between gap-4 border border-stone-200 rounded-2xl p-5">
          <div className="min-w-0">
            <p className="text-sm font-bold text-astroDark">Slab pricing active</p>
            <p className="text-[11px] text-stone-500 mt-0.5">
              {isActive ? 'These slab prices are live for new bookings.' : 'Slab pricing is paused — turn it on to apply these prices.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isActive}
            aria-label="Slab pricing active"
            onClick={() => setValue('is_active', !isActive, { shouldDirty: true })}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${isActive ? 'bg-[#A67C00]' : 'bg-stone-300'}`}
          >
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${isActive ? 'left-6' : 'left-1'}`} />
          </button>
        </div>
      </div>

      {/* ── Save bar ── */}
      <div className="dash-card p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          {banner ? (
            <p className={`text-xs font-semibold ${banner.tone === 'ok' ? 'text-green-600' : 'text-red-500'}`}>{banner.text}</p>
          ) : (
            <p className="text-xs text-stone-500">Review your slab prices, then save — new bookings use the updated pricing.</p>
          )}
        </div>
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="shine-btn bg-gradient-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.2em] px-6 py-3 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 shrink-0"
        >
          {saveMutation.isPending ? 'Saving…' : 'Save Pricing'}
        </button>
      </div>

      {/* ── Success toast ── */}
      {toast && (
        <div role="status" aria-live="polite" className="dash-toast flex items-center gap-2.5 bg-[#3D0A0A] text-white text-sm font-semibold pl-4 pr-5 py-3 rounded-xl shadow-2xl shadow-black/30">
          <span className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center shrink-0">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          {toast}
        </div>
      )}
    </form>

    {/* Raju Maharaj's second consultation - own form, own save. */}
    <GemstoneConsultationSettings />
    </div>
  )
}
